import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShoppingBag, Wallet, AlertTriangle, CheckCircle2, ArrowRight } from "lucide-react";
import api, { money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PortalLayout from "@/components/portal/PortalLayout";
import StatusBadge from "@/components/StatusBadge";
import { Progress } from "@/components/ui/progress";

export default function PortalDashboard() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    api.get("/my/summary").then((r) => setSummary(r.data)).catch(() => {});
    api.get("/my/orders").then((r) => setOrders(r.data)).catch(() => {});
  }, []);

  const s = summary || {};
  const pct = s.total_installments ? Math.round((s.paid_installments / s.total_installments) * 100) : 0;

  const stats = [
    { label: "Total Payable", value: money(s.total_payable), icon: Wallet },
    { label: "Total Paid", value: money(s.total_paid), icon: CheckCircle2 },
    { label: "Remaining", value: money(s.remaining), icon: ShoppingBag },
    { label: "Overdue", value: money(s.overdue_amount), icon: AlertTriangle, danger: s.overdue_amount > 0 },
  ];

  return (
    <PortalLayout title={`Welcome, ${user?.name || "Customer"}`}>
      <p className="-mt-3 mb-5 text-sm text-muted-foreground">Customer ID: <span className="font-mono font-semibold text-foreground">{user?.customer_no || "—"}</span></p>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((st) => (
          <div key={st.label} className={`rounded-xl border bg-card p-5 ${st.danger ? "border-destructive/40" : "border-border"}`} data-testid={`portal-stat-${st.label.toLowerCase().replace(/ /g, "-")}`}>
            <div className="flex items-center justify-between"><span className="text-sm text-muted-foreground">{st.label}</span><st.icon className={`h-4 w-4 ${st.danger ? "text-destructive" : "text-primary"}`} /></div>
            <p className={`mt-2 font-display text-2xl font-extrabold ${st.danger ? "text-destructive" : ""}`}>{st.value}</p>
          </div>
        ))}
      </div>

      <div className="mt-6 rounded-xl border border-border bg-card p-6">
        <div className="flex items-center justify-between">
          <h3 className="font-display font-semibold">Installments Progress</h3>
          <span className="font-display text-lg font-extrabold text-primary" data-testid="installment-progress">{s.paid_installments || 0} / {s.total_installments || 0} Paid</span>
        </div>
        <Progress value={pct} className="mt-4 h-3" />
        {s.next_due && <p className="mt-3 text-sm text-muted-foreground">Next installment: <span className="font-semibold text-foreground">{money(s.next_due.amount)}</span> due <span className="font-semibold text-foreground">{s.next_due.due_date}</span> ({s.next_due.order_no})</p>}
      </div>

      <div className="mt-6">
        <div className="mb-3 flex items-center justify-between"><h3 className="font-display font-semibold">Recent Orders</h3><Link to="/portal/orders" className="text-sm font-semibold text-primary hover:underline">View all →</Link></div>
        {orders.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border p-10 text-center text-muted-foreground">
            No orders yet. <Link to="/products" className="font-semibold text-primary">Browse products</Link> to get started.
          </div>
        ) : (
          <div className="grid gap-3">
            {orders.slice(0, 4).map((o) => (
              <Link key={o.id} to={`/portal/orders/${o.id}`} className="flex items-center gap-4 rounded-xl border border-border bg-card p-4 hover:border-primary/50" data-testid={`portal-order-${o.id}`}>
                <img src={o.product_image} alt={o.product_name} className="h-14 w-14 rounded-lg object-cover" />
                <div className="min-w-0 flex-1"><p className="truncate font-semibold">{o.product_name}</p><p className="text-xs text-muted-foreground">{o.order_no} · {money(o.monthly_installment)}/mo</p></div>
                <StatusBadge status={o.status} />
                <ArrowRight className="h-4 w-4 text-muted-foreground" />
              </Link>
            ))}
          </div>
        )}
      </div>
    </PortalLayout>
  );
}
